<template>
  <div class="events-display" ref="eventsDisplay">
    <div class="events-display__background" :class="{'events-display__background--end-warning': showEndWarning, 'events-display__background--free': !currentEvent}"></div>
    <div class="events" ref="events">
      <div class="events__event events__event--current" :class="{'events__event--no-following-event': !currentEvent && !nextEvent, 'events__event--current-free': !currentEvent, 'events__event--current-ad-hoc': canEndAdHoc}">
        <div v-if="currentEvent">
          {{ eventSummary(currentEvent) }}

          <div v-if="currentEventConferenceData && showJoinLinks" class="events__join">
            <a :href="currentEventConferenceData.joinUri" class="button button--slim button--black" target="_blank">
              <img :src="`//images.weserv.nl/?url=${currentEventConferenceData.iconUri}`" class="button__icon"/>
              Join {{ currentEventConferenceData.conferenceSolutionName }}</a>
          </div>

          <wfh-list v-if="showPersonListLink(currentEvent)" :g-cal-api="gCalApi" :event="currentEvent"/>

          <div class="events__event-details">
            {{ eventTimesString(currentEvent) }}
            &bull;
            <a v-if="showPersonListLink(currentEvent)" href="#" class="events-display__link" @click.prevent="showPersonList(currentEvent)">{{ eventCreator(currentEvent) }}</a>
            <span v-else>{{ eventCreator(currentEvent) }}</span>

          </div>
        </div>
        <div v-else-if="nextEvent">
          Free for {{ timeUntilNextEvent }}
        </div>
        <div v-else>
          Free for the rest of the day
        </div>
      </div>

      <div v-if="!currentEvent && allowCheckIn">
        <button class="button check-in-button" :class="{'check-in-button--no-following-event': !nextEvent}" v-on:click="showCheckInLayer(); playButtonSound();">Check in</button>
      </div>

      <div v-if="canEndAdHoc">
        <button class="button button--black end-now-button" :class="{'end-now-button--no-following-event': !nextEvent}" v-on:click="endAdHocEventNow(); playButtonSound();">End now</button>
      </div>

      <div class="events__event events__event--next">
        <div v-if="nextEvent && (nextEventIsAdjacent || !currentEvent)">
          Next: {{ eventSummary(nextEvent) }}

          <div v-if="nextEventConferenceData && showJoinLinks" class="events__join">
            <a :href="nextEventConferenceData.joinUri" class="button button--slim" :class="{'button--black': currentEvent}" target="_blank">
            <img :src="`//images.weserv.nl/?url=${nextEventConferenceData.iconUri}`" class="button__icon"/>
            Join {{ nextEventConferenceData.conferenceSolutionName }}</a>
          </div>

          <div class="events__event-details events__event-details--next">
            {{ eventTimesString(nextEvent) }}
            &bull;
            <a v-if="showPersonListLink(nextEvent)" href="#" class="events-display__link" @click.prevent="showPersonList(nextEvent)">{{ eventCreator(nextEvent) }}</a>
            <span v-else>{{ eventCreator(nextEvent) }}</span>
          </div>
        </div>

        <div v-else-if="currentEvent && nextEvent">
          Then free for {{ timeBetweenCurrentAndNextEvent }}
        </div>
        <div v-else-if="currentEvent && !currentEventIsAllDay">
          Then free for the rest of the day
        </div>
      </div>
    </div>

    <check-in v-if="checkInLayerVisible" :g-cal-api="gCalApi" :now="now" :next-event="nextEvent" :calendar-id="calendarId" v-on:check-in-event-added="handleCheckInEventAdded($event)" v-on:close-check-in-layer="hideCheckInLayer()"/>

    <event-person-list v-if="personListVisible" :g-cal-api="gCalApi" :event="eventForPersonList" v-on:close-person-list="hidePersonList()"/>
  </div>
</template>

<script>
  import TimeMixins from './TimeMixins.vue';
  import ButtonSoundMixins from './ButtonSoundMixins.vue';
  import StringMixins from './StringMixins.vue';
  import CheckIn from './CheckIn.vue';
  import EventPersonList from './EventPersonList.vue'
  import WFHList from './WFHList.vue'

  export default {
    name: 'EventDisplay',
    components: {
      'check-in': CheckIn,
      'event-person-list': EventPersonList,
      'wfh-list': WFHList
    },
    mixins: [TimeMixins, ButtonSoundMixins, StringMixins],
    props: {
      gCalApi: Object,
      calendarId: String,
      eventsRefreshIntervalMs: Number,
      warnBeforeEndMs: Number,
      readyToFetchEvents: Boolean,
      showEventNames: Boolean,
      now: Date,
      allowCheckIn: Boolean
    },
    data: function() {
      return {
        userEmail: [],
        events: [],
        eventsRefreshInterval: null,
        lastWarnedEventId: '',
        checkInLayerVisible: false,
        personListVisible: false,
        warningSound: new Audio('sounds/end-warning.wav'),
        eventForPersonList: null
      }
    },
    watch: {
      calendarId: function() {
        if(this.calendarId) {
          this.fetchEvents();
        }
      },
      now: function() {
        this.resizeText();
        this.playWarningSound();
      },
      readyToFetchEvents: function() {
        if(this.readyToFetchEvents) {
          this.startRefreshingEvents()
        } else {
          this.stopRefreshingEvents();
        }
      },
      currentEvent: function() {
        const app = this;
        this.$nextTick()
          .then(function () {
            app.resizeText();
          });
      },
      nextEvent: function() {
        const app = this;
        this.$nextTick()
          .then(function () {
            app.resizeText();
          });

        this.$emit('next-event-changed', this.nextEvent);
      }
    },
    computed: {
      currentEvent: function() {
        return this.events.filter((e) => {
          const roomHasAccepted = this.roomHasAcceptedEvent(e);

          if(!roomHasAccepted) {
            return false;
          }

          // If it has a date property it's an all-day event with no start or end dateTime
          if(e.start.hasOwnProperty('date')) {
            return true;
          }

          const start = new Date(e.start.dateTime);
          const end = new Date(e.end.dateTime);
          return start <= this.now && end > this.now;
        })[0];
      },
      nextEvent: function() {
        if(this.currentEventIsAllDay) {
          return null;
        }

        return this.events.filter((e) => {
          const roomHasAccepted = this.roomHasAcceptedEvent(e);

          if(!roomHasAccepted) {
            return false;
          }

          const start = new Date(e.start.dateTime);
          return start > this.now;
        })[0];
      },
      currentEventConferenceData: function() {
        return this.conferenceDataForEvent(this.currentEvent);
      },
      nextEventConferenceData: function() {
        return this.conferenceDataForEvent(this.nextEvent);
      },
      currentEventIsAllDay: function() {
        return this.currentEvent && this.currentEvent.start.hasOwnProperty('date');
      },
      canEndAdHoc: function() {
        return this.currentEvent && this.currentEvent.summary === "Ad hoc meeting" && this.currentEvent.creator.email === this.userEmail;
      },
      nextEventIsAdjacent: function() {
        return this.currentEvent && this.nextEvent && (new Date(this.currentEvent.end.dateTime)).getTime() >= (new Date(this.nextEvent.start.dateTime).getTime());
      },
      showEndWarning: function() {
        const now = new Date();
        return this.currentEvent && this.nextEvent && (new Date(this.nextEvent.start.dateTime) - now <= this.warnBeforeEndMs); //There's an ongoing event and the start time of the next event is close
      },
      timeUntilNextEvent: function() {
        const diff = new Date(this.nextEvent.start.dateTime) - new Date();
        return this.timestampDiffToFuzzyString(diff);
      },
      timeBetweenCurrentAndNextEvent: function() {
        const diff = new Date(this.nextEvent.start.dateTime) - new Date(this.currentEvent.end.dateTime)
        return this.timestampDiffToFuzzyString(diff);
      }
    },
    methods: {
      fetchEvents: function() {
        if(!this.calendarId) {
          return;
        }

        const app = this;

        this.gCalApi.client.calendar.events.list({
          'calendarId': this.calendarId,
          'timeMin': (new Date()).toISOString(),
          'timeMax': (new Date((new Date()).setHours(23,59,59))).toISOString(), //Midnight tonight
          'showDeleted': false,
          'singleEvents': true,
          'maxResults': 10,
          'orderBy': 'startTime'
        }).then(function(response) {
          //Stub in substitute values for private events
          let events = response.result.items.map((e) => ({
            'summary': 'Private meeting',
            'attendees': [{'email': app.calendarId, 'responseStatus': 'accepted'}],
            ...e}));
          app.events = events;
        });
      },
      startRefreshingEvents: function() {
        const app = this;
        this.eventsRefreshInterval = setInterval(function() {
          app.fetchEvents();
        }, this.eventsRefreshIntervalMs);

        app.fetchEvents();
      },
      stopRefreshingEvents: function() {
        clearInterval(this.eventsRefreshInterval);
      },
      roomHasAcceptedEvent: function(event) {
        return event.attendees.filter((a) => a.email === this.calendarId && a.responseStatus === 'accepted').length > 0;
      },
      eventTimesString: function(event) {
        if(event.start.hasOwnProperty('date')) {
          return 'All day'
        } else {
          return `${this.timeFromDateTime(event.start.dateTime)}–${this.timeFromDateTime(event.end.dateTime)}`;
        }
      },
      eventCreator: function(event) {
        // For ad-hoc events (booked via CheckIn.vue) we overwrite the event creator to "You", as we don't know the actual person who made the booking
        if(event.summary === "Ad hoc meeting") {
          return "You";
        }

        if(event.creator.hasOwnProperty('displayName')) {
          return event.creator.displayName;
        } else {
          return this.nameFromEmailAddress(event.creator.email)
        }
      },
      resizeText: function() {
        const eventsDiv = this.$refs.events;
        const displayDiv = this.$refs.eventsDisplay;

        let currentSize = 1.0;
        eventsDiv.style.fontSize = `${currentSize}em`;

        // Check if the combined height of all the text on the screen is taller than it should be.
        // Reduce the size until it fits.
        while(eventsDiv.offsetHeight > displayDiv.offsetHeight - parseFloat(window.getComputedStyle(displayDiv, null).getPropertyValue("padding-top"))*2) {
          eventsDiv.style.fontSize = `${currentSize-=0.01}em`;
          if(currentSize<0){ break; }
        }
      },
      playWarningSound: function() {
        if(this.showEndWarning && this.lastWarnedEventId != this.currentEvent.id && this.playSounds) {
          this.warningSound.play();
          this.lastWarnedEventId = this.currentEvent.id;
        }
      },
      showCheckInLayer: function() {
        this.checkInLayerVisible = true;
      },
      hideCheckInLayer: function() {
        this.checkInLayerVisible = false;
      },
      handleCheckInEventAdded: function(event) {

        // Stop refreshing events for a few seconds, because it can take a bit of time for the auto-accept on the room calendar to happen
        this.stopRefreshingEvents();

        const app = this;
        setTimeout(function() {
          app.startRefreshingEvents();
        }, 10000);

        this.hideCheckInLayer();
        this.events.push(event);
      },
      endAdHocEventNow: function() {
        const request = this.gCalApi.client.calendar.events.patch({
          'calendarId': 'primary',
          'eventId': this.currentEvent.id,
          'end': {
            'dateTime': this.now.toISOString()
          }
        });

        request.execute();

        // Remove the ad-hoc event from the events list so we don't have to wait for the events to refresh for the UI to update
        // (it should always be the current event)
        this.events = this.events.filter((e) => e.id != this.currentEvent.id);
      },
      eventSummary: function(event) {
        if(this.showEventNames) {
          return event.summary;
        } else {
          const start = (event.start.hasOwnProperty('dateTime')) ? event.start.dateTime : event.start.date;
          const end = (event.end.hasOwnProperty('dateTime')) ? event.end.dateTime : event.end.date;
          const diff = new Date(end) - new Date(start)
          return this.timestampDiffToPreciseString(diff) + ' meeting';
        }
      },
      showPersonList: function(event) {
        this.eventForPersonList = event;
        this.personListVisible = true;
      },
      hidePersonList: function() {
        this.eventForPersonList = null;
        this.personListVisible = false;
      },
      showPersonListLink: function(event) {
        return !(event.hasOwnProperty('visibility') && event.visibility === 'private') && !(event.summary === "Ad hoc meeting") && this.showEventNames;
      },
      conferenceDataForEvent: function(event) {
        if(!event || !event.conferenceData || !event.conferenceData) {
          return null
        }

        const iconUri = event.conferenceData.conferenceSolution.iconUri;
        const conferenceSolutionName = event.conferenceData.conferenceSolution.name;
        const videoEntryPoint = event.conferenceData.entryPoints.find(ep => ep.entryPointType === 'video')

        if(videoEntryPoint) {
          return {
            iconUri: iconUri,
            conferenceSolutionName: conferenceSolutionName,
            joinUri: videoEntryPoint.uri
          }
        } else {
          return null;
        }
      }
    },
    mounted: function() {
      const app = this;
      this.gCalApi.client.people.people.get({
        'resourceName': 'people/me',
        'requestMask.includeField': 'person.emailAddresses'
      }).then(function(response) {
          app.userEmail = response.result.emailAddresses[0].value;
        });

      if(this.readyToFetchEvents) {
        this.startRefreshingEvents();
      } else {
        this.stopRefreshingEvents();
      }
    }
  }
</script>

<style scoped lang="scss">
@import '../styles/components/colours';

.events-display {
  height: 100%;
  padding: 6em;
  box-sizing: border-box;

  &__background {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: $black;

    &--free {
      background: $green;
    }

    &--end-warning {
      border: 3em solid $orange;
      box-shadow: inset 0 0 10em $orange;
    }
  }

  &__link {
    color: $white;
  }

  &__conference-icon {
    height: 1em;
  }
}

.events {
  color: $white;
  text-align: center;
  line-height: 1.1;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  box-sizing: border-box;
  padding: 0 10em;

  &__join {
    font-size: 0.5em;
    margin: 0.3em 0 1em 0;
  }

  &__event {
    &--current {
      font-size: 20em;
      padding-bottom: 0.4em;
      font-weight: 700;
    }

    &--next {
      font-size: 12em;
    }

    &--current-free {
      padding-bottom: 0em;
    }

    &--current-ad-hoc {
      padding-bottom: 0.1em;
    }
  }

  &__wfh-list {
    font-size: 0.6em;
    font-weight: 400;
  }

  &__event-details {
    font-size: 0.6em;
    opacity: 0.7;
    font-weight: 400;
    margin-top: 0.3em;
  }

  &__event-details--next {
    font-size: 0.7em;
  }
}

.check-in-button {
  display: block;
  margin: 0 auto;
  font-size: 10em;
  margin-bottom: 1.1em;

  &--no-following-event {
    margin-bottom: 0;
    margin-top: 0.5em;
  }
}

.end-now-button {
  display: block;
  margin: 0 auto;
  font-size: 10em;
  margin-bottom: 1.1em;
}
</style>
